import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  SimpleChanges,
} from '@angular/core';
import { EduContent, Permissions, ResultInterface, ResultStatusEnum } from '@campus/dal';
import { ENVIRONMENT_METHODS_FEATURE_TOKEN, EnvironmentMethodsFeatureInterface } from '@campus/environment';
import {
  CONTENT_BUNDLE_ACTIONS_SERVICE_TOKEN,
  CONTENT_OPEN_ACTIONS_SERVICE_TOKEN,
  CONTENT_TASK_ACTIONS_SERVICE_TOKEN,
  ContentActionInterface,
  ContentBundleActionsServiceInterface,
  ContentOpenActionsServiceInterface,
  ContentTaskActionsServiceInterface,
  EduContentSearchResultInterface,
  PermissionService,
} from '@campus/shared';
import '@diekeure/dcr-components/dcr-badge.js';
import '@diekeure/dcr-components/dcr-icon.js';
import '@diekeure/dcr-components/dcr-inline-icon-button.js';
import '@diekeure/dcr-components/dcr-menu-item.js';
import '@diekeure/dcr-components/dcr-menu.js';
import '@diekeure/dcr-components/dcr-score.js';
import '@diekeure/dcr-components/dcr-sub-menu.js';
import { BehaviorSubject, combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { map, shareReplay, throttleTime } from 'rxjs/operators';

@Component({
  standalone: false,
  // tslint:disable-next-line
  selector: 'campus-toc-result-list-item',
  templateUrl: './toc-result-list-item.component.html',
  styleUrls: ['./toc-result-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TocResultListItemComponent implements OnInit, OnDestroy, OnChanges {
  private _inBasket = false;
  public element = inject(ElementRef).nativeElement;

  @HostBinding('attr.data-cy')
  dataCy = 'toc-result-list-item';

  // @HostBinding('class')
  // defaultClasses = ['toc-result-list-item', 'relative'];

  // @HostBinding('class.toc-result-list-item--selected')
  @Input()
  selected = false;

  @Input() canSelect = true;

  @Input() isFavorite = false;

  @Input() type: 'list' | 'card' = 'card';

  attemptScores: number[] = [];

  @Input()
  get inBasket() {
    return this._inBasket;
  }
  set inBasket(value) {
    this._inBasket = value;
    if (value) {
      this.selected = true;
    }
  }

  @Input() throttleTime = 500;
  @Input() data: EduContentSearchResultInterface;
  fullEduContentName: string;

  @Output() selectionChange = new EventEmitter<{ value: EduContentSearchResultInterface; checked: boolean }>();
  @Output() toggleFavorite = new EventEmitter<EduContent>();
  @Output() clickLevelAttempts = new EventEmitter<EduContentSearchResultInterface>();

  Permissions = Permissions.settings;
  public TaskPermissions = Permissions.tasks;

  public canManageTaskContentV2$: Observable<boolean>;
  public canShowSelect$: Observable<boolean>;

  private inputState$ = new BehaviorSubject<{ taskAllowed: boolean; canSelect: boolean; inBasket: boolean }>({
    taskAllowed: false,
    canSelect: true,
    inBasket: false,
  });

  actions: ContentActionInterface[];
  primaryAction: ContentActionInterface;
  secondaryActions: ContentActionInterface[];

  private clicks = new Subject<ContentActionInterface>();
  private subscription: Subscription;

  constructor(
    @Inject(ENVIRONMENT_METHODS_FEATURE_TOKEN)
    private methodEnvironment: EnvironmentMethodsFeatureInterface,
    @Inject(CONTENT_OPEN_ACTIONS_SERVICE_TOKEN)
    private contentOpenActionsService: ContentOpenActionsServiceInterface,
    @Optional()
    @Inject(CONTENT_TASK_ACTIONS_SERVICE_TOKEN)
    private contentTaskActionsService: ContentTaskActionsServiceInterface,
    @Optional()
    @Inject(CONTENT_BUNDLE_ACTIONS_SERVICE_TOKEN)
    private contentBundleActionsService: ContentBundleActionsServiceInterface,
    private permissionService: PermissionService
  ) {}

  ngOnInit() {
    this.subscription = this.clicks
      .pipe(throttleTime(this.throttleTime))
      .subscribe((action) => this.handleAction(action));

    this.canManageTaskContentV2$ = this.permissionService
      .hasPermission$(this.TaskPermissions.MANAGE_TASK_CONTENT_V2)
      .pipe(shareReplay({ refCount: true, bufferSize: 1 }));

    const canManageTasksOrBundles$ = this.permissionService
      .hasPermission$([[this.Permissions.MANAGE_TASKS, this.Permissions.MANAGE_BUNDLES]])
      .pipe(shareReplay({ refCount: true, bufferSize: 1 }));

    this.canShowSelect$ = combineLatest([
      this.inputState$,
      this.canManageTaskContentV2$,
      canManageTasksOrBundles$,
    ]).pipe(
      map(
        ([{ taskAllowed, canSelect, inBasket }, canManageTaskContentV2, canManageTasksOrBundles]) =>
          taskAllowed && canSelect && (!inBasket || canManageTaskContentV2) && canManageTasksOrBundles
      ),
      shareReplay({ refCount: true, bufferSize: 1 })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data && this.data) {
      this.update();
      this.mapAttemptScores();
    }
    if (changes.data || changes.canSelect || changes.inBasket) {
      this.inputState$.next({
        taskAllowed: !!this.data?.eduContent?.taskAllowed,
        canSelect: this.canSelect,
        inBasket: this.inBasket,
      });
    }
  }

  public update() {
    this.setupActions();
    this.fullEduContentName = this.getFullEduContentName();
    if (this.data.sessions) {
      this.data.sessions = this.data.sessions.sort((a, b) => +a.lastUpdated - +b.lastUpdated);
    }
  }

  private mapAttemptScores(): void {
    if (!this.data?.bestResult?.attempts?.length) {
      this.attemptScores = [];
      return;
    }
    this.attemptScores = this.data.bestResult.attempts.map((attempt) => attempt.score);
  }

  public onSelectionChange(checked: boolean, searchResult: EduContentSearchResultInterface) {
    this.selected = checked;
    this.selectionChange.emit({ value: searchResult, checked });
  }

  public onToggleFavorite() {
    this.toggleFavorite.emit(this.data.eduContent);
  }

  private handleAction(action: ContentActionInterface) {
    action.handler(this.data.eduContent, action.options);
  }

  private setupActions(): void {
    this.actions = [...this.getTaskActions(), ...this.getBundleActions(), ...this.getContentActions()];

    this.primaryAction = this.actions.find((action) => action.isPrimary);
    this.secondaryActions = this.actions.filter((action) => !action.isPrimary);
  }

  private getContentActions(): ContentActionInterface[] {
    const result = this.data.bestResult || this.data.result;
    const isCompletedExercise = this.isCompletedExercise(result);

    return isCompletedExercise
      ? this.getActionsForCompletedExercise(result)
      : this.contentOpenActionsService.getActionsForContent(this.data.eduContent, result);
  }

  private isCompletedExercise(result: ResultInterface | undefined): boolean {
    return (
      this.data.eduContent.isExercise &&
      result &&
      [ResultStatusEnum.STATUS_COMPLETED, ResultStatusEnum.STATUS_OPENED].includes(result.status)
    );
  }

  private getActionsForCompletedExercise(result: ResultInterface): ContentActionInterface[] {
    const regularActions = this.contentOpenActionsService.getActionsForContent(this.data.eduContent, result);
    const reviewActions = this.contentOpenActionsService.getActionsForContent(this.data.eduContent, result, {
      forReview: true,
    });

    const reviewAction = this.findReviewAction(reviewActions);

    if (reviewAction) {
      const secondaryReviewAction = this.createSecondaryReviewAction(reviewAction, result);
      return [...regularActions, secondaryReviewAction];
    }

    return regularActions;
  }

  private findReviewAction(reviewActions: ContentActionInterface[]): ContentActionInterface | undefined {
    return reviewActions.find((action) => action.label.includes('Herbekijk') || action.label.includes('review'));
  }

  private createSecondaryReviewAction(
    reviewAction: ContentActionInterface,
    result: ResultInterface
  ): ContentActionInterface {
    return {
      ...reviewAction,
      isPrimary: false,
      options: { forResult: result },
    };
  }

  private getTaskActions(): ContentActionInterface[] {
    if (!this.data.addTaskActions) return [];
    if (!this.contentTaskActionsService) return [];

    return this.contentTaskActionsService.getTaskActionsForEduContent(this.data.eduContent, this.data.inBasket);
  }

  private getBundleActions(): ContentActionInterface[] {
    if (!this.data.addBundleActions) return [];
    if (!this.contentBundleActionsService) return [];

    return this.contentBundleActionsService.getBundleActionsForEduContent(this.data.eduContent, this.data.inBasket); //todo: doesn't work yet?
  }

  private getFullEduContentName(): string {
    const base = this.data.eduContent.name;
    const shouldAddEduNet = this.data.eduContent.eduNetNames.length && this.methodEnvironment.showEduNetNames;
    if (!shouldAddEduNet) return base;

    return `${base} - ${this.data.eduContent.eduNetNames.join(', ')}`;
  }

  onActionClick(action: ContentActionInterface) {
    this.clicks.next(action);
  }

  onClickLevelAttempts() {
    this.clickLevelAttempts.emit(this.data);
  }
}
